import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { sfx } from "@/lib/sounds";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const SessionContext = createContext(null);

const RECOVERY_KEY = "okfbi_last_session_seen";

export function SessionProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recovery, setRecovery] = useState(null); // { case_id, revealed_count, timer_seconds }

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/session");
      setSession(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 8000); // poll to sync image-ready & votes
    return () => clearInterval(t);
  }, [refresh]);

  // Detect resumable investigation on first mount (auto-recovery)
  // Skip on the /overlay OBS Browser Source route and on /login gate.
  useEffect(() => {
    if (!session || recovery !== null) return;
    if (typeof window !== "undefined") {
      const p = window.location.pathname;
      // Skip recovery on public surfaces
      if (p === "/" || p === "/terms" || p === "/privacy" ||
          p.startsWith("/overlay") || p.startsWith("/login")) return;
    }
    let seen = null;
    try { seen = sessionStorage.getItem(RECOVERY_KEY); } catch {}
    if (seen === "1") return; // already handled in this tab
    const cid = session.current_case_id;
    if (!cid) { try { sessionStorage.setItem(RECOVERY_KEY, "1"); } catch {} return; }
    const revealed = (session.revealed || {})[cid] || {};
    const total = ["evidence", "suspects", "locations", "timeline", "interrogations", "forensics", "cctv"]
      .reduce((n, k) => n + (revealed[k]?.length || 0), 0);
    const hasResult = !!(session.case_results || {})[cid];
    if (total > 0 && !hasResult) {
      setRecovery({ case_id: cid, revealed_count: total });
    } else {
      try { sessionStorage.setItem(RECOVERY_KEY, "1"); } catch {}
    }
  }, [session, recovery]);

  const dismissRecovery = (resume) => {
    try { sessionStorage.setItem(RECOVERY_KEY, "1"); } catch {}
    if (!resume) {
      // Reset only the resumable case; full-session reset is STREAMER/OWNER-only.
      const caseId = recovery?.case_id;
      const reset = caseId
        ? api.post(`/session/case-full-reset/${caseId}`)
        : Promise.resolve();
      reset.then(refresh);
    }
    setRecovery(null);
  };

  const updateSession = async (patch) => {
    try {
      const { data } = await api.post("/session", patch);
      setSession((prev) => ({
        ...(prev || {}),
        ...(data || {}),
        images_ready: data?.images_ready || prev?.images_ready,
      }));
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };

  const reveal = async (case_id, kind, item_id) => {
    try {
      const { data } = await api.post("/session/reveal", { case_id, kind, item_id });
      if (!data.already) sfx.evidenceReveal();
      await refresh();
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };
  const unreveal = async (case_id, kind, item_id) => {
    try {
      await api.post("/session/unreveal", { case_id, kind, item_id });
      sfx.evidenceUndo();
      await refresh();
    } catch (e) {
      console.error(e);
    }
  };
  const undoLastReveal = async () => {
    try {
      const { data } = await api.post("/session/undo-reveal", { steps: 1 });
      if (data.undone && data.undone.length) sfx.evidenceUndo();
      await refresh();
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };
  const revealAll = async (case_id, kind) => {
    try {
      await api.post("/session/reveal-all", { case_id, kind });
      sfx.notification();
      await refresh();
    } catch (e) {
      console.error(e);
    }
  };
  const addParticipant = async (name) => {
    try {
      const { data } = await api.post("/session/participants", { name });
      setSession((s) => ({ ...s, participants: data.participants }));
      sfx.click();
    } catch (e) {
      console.error(e);
    }
  };
  const removeParticipant = async (name) => {
    try {
      await api.delete(`/session/participants/${encodeURIComponent(name)}`);
      await refresh();
    } catch (e) {
      console.error(e);
    }
  };
  const castVote = async (participant, suspect_id) => {
    try {
      await api.post("/session/votes", { participant, suspect_id });
      sfx.click();
      await refresh();
    } catch (e) {
      console.error(e);
    }
  };
  const clearVote = async (participant) => {
    try {
      await api.delete(`/session/votes/${encodeURIComponent(participant)}`);
      await refresh();
    } catch (e) {
      console.error(e);
    }
  };
  const revealSolution = async (case_id) => {
    try {
      const { data } = await api.post("/session/reveal-solution", { case_id });
      sfx.caseSolved();
      await refresh();
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };
  const resetSession = async () => {
    try {
      await api.post("/session/reset", { keep_participants: true });
      await refresh();
    } catch (e) {
      console.error(e);
    }
  };
  const resetCase = async (case_id) => {
    if (!case_id) return false;
    try {
      await api.post(`/session/case-full-reset/${encodeURIComponent(case_id)}`);
      sfx.pageFlip();
      await refresh();
      return true;
    } catch (e) {
      console.error(e);
      toast.error("فشل تصفير القضية — تحقق من الاتصال وحاول مجدداً");
      return false;
    }
  };
  const accuseSuspect = async (case_id, suspect_id, extra = {}) => {
    try {
      const { data } = await api.post("/session/accuse", { case_id, suspect_id, ...extra });
      if (data.is_correct) sfx.caseSolved(); else sfx.accusation();
      await refresh();
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };
  const revealCaseTruth = async (case_id) => {
    try {
      const { data } = await api.post(`/session/case-result/${case_id}/reveal`);
      sfx.caseSolved();
      await refresh();
      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  };
  const clearCaseResult = async (case_id) => {
    try {
      await api.delete(`/session/case-result/${case_id}`);
      sfx.evidenceUndo();
      await refresh();
    } catch (e) {
      console.error(e);
    }
  };
  // Single reset path — FinalRevealTab's legacy name delegates here so both
  // callers share encoding, toast, and boolean feedback.
  const fullCaseReset = async (case_id) => resetCase(case_id);

  return (
    <SessionContext.Provider value={{
      session, loading, refresh, updateSession, reveal, unreveal, undoLastReveal, revealAll,
      addParticipant, removeParticipant, castVote, clearVote, revealSolution, resetSession, resetCase,
      accuseSuspect, revealCaseTruth, clearCaseResult, fullCaseReset,
    }}>
      {children}
      {createPortal(
        <AnimatePresence>
          {recovery && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-[999] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
              dir="rtl" data-testid="recovery-modal">
              <motion.div initial={{ scale: 0.94, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94 }}
                className="panel gold-border max-w-lg w-full p-8 text-center">
                <div className="text-[10px] tracking-[0.4em] text-gold font-mono-ar mb-2">استعادة الجلسة</div>
                <h3 className="font-cairo text-3xl text-white mb-3">تم العثور على جلسة تحقيق غير مكتملة</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-6">
                  جلسة نشطة على القضية <span className="text-gold font-cairo">{recovery.case_id?.toUpperCase()}</span> مع <span className="text-gold">{recovery.revealed_count}</span> عنصر مكشوف. هل تريد المتابعة؟
                </p>
                <div className="flex gap-3 justify-center">
                  <button onClick={() => dismissRecovery(true)} className="btn-primary" data-testid="recovery-resume">متابعة التحقيق</button>
                  <button onClick={() => dismissRecovery(false)} className="btn-ghost text-red-400 border-red-900/60" data-testid="recovery-new">بدء جلسة جديدة</button>
                </div>
                <div className="text-[9px] text-zinc-600 mt-5 tracking-[0.3em] font-cairo">أوكي إف بي آي · حفظ تلقائي</div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </SessionContext.Provider>
  );
}

export const useSession = () => useContext(SessionContext);

// Helper: get safe backend base URL, ignoring localhost in production
export function getBackendBase() {
  let base = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (typeof window !== "undefined") {
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    if (!isLocal && (base.includes("localhost") || base.includes("127.0.0.1"))) {
      if (/^(\d{1,3}\.){3}\d{1,3}$/.test(window.location.hostname)) {
        base = base.replace(/localhost|127\.0\.0\.1/, window.location.hostname);
      } else {
        base = "";
      }
    }
  }
  return base;
}

// Helper: build asset URL for images. Robust in Production against localhost, prefixes, and uploads
export function assetUrl(path) {
  if (!path) return "";
  const backendBase = getBackendBase();

  // If path is already a full URL (http / https)
  if (typeof path === "string" && (path.startsWith("http://") || path.startsWith("https://"))) {
    // If it points to localhost / 127.0.0.1 in production, rewrite to backendBase or safe relative path
    if (path.includes("localhost") || path.includes("127.0.0.1")) {
      try {
        const u = new URL(path);
        let pathname = u.pathname;
        pathname = pathname.replace(/^\/api\/generated_images\//, "/api/images/");
        pathname = pathname.replace(/^\/generated_images\//, "/api/images/");
        if (pathname.startsWith("/uploads/") && !pathname.startsWith("/api/uploads/")) {
          pathname = `/api${pathname}`;
        }
        return `${backendBase}${pathname}${u.search}`;
      } catch {
        return path;
      }
    }
    return path;
  }

  // Normalize relative paths
  let p = typeof path === "string" ? path.trim() : "";
  if (!p) return "";
  if (!p.startsWith("/")) p = `/${p}`;

  // Rewrite /api/generated_images/ or /generated_images/ -> /api/images/
  p = p.replace(/^\/api\/generated_images\//, "/api/images/");
  p = p.replace(/^\/generated_images\//, "/api/images/");

  // Rewrite /uploads/ -> /api/uploads/
  if (p.startsWith("/uploads/")) {
    p = `/api${p}`;
  }

  return `${backendBase}${p}`;
}
